/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { QRCodeCanvas } from 'qrcode.react';

import { formatAndNotificateError } from 'utils';
import { Button, Modal } from 'components/sdk';

// eslint-disable-next-line no-restricted-imports
import { Spin, message } from 'antd';

import { useBusinessLoanContext } from 'contexts/BusinessLoanContext';
import { CopyOutlined } from '@ant-design/icons';
import GET_BADESUL_PIX from './graphql/getBadesulPix.gql';
import CREATE_BADESUL_PIX from './graphql/createBadesulPix.gql';
import CONFIRM_BADESUL_PIX_PAYMENT from './graphql/confirmBadesulPixPayment.gql';

import './styles.less';

/**
 * @type {React.FC<{
 *  visible: boolean
 *  onClose: () => void
 *  loan: {
 *    id: string
 *  }
 * }>}
 */
const BadesulPixModal = ({ visible, onClose, loan: { id } }) => {
  const { contextRefetch } = useBusinessLoanContext();

  const [currentPix, setCurrentPix] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const countdownAsDate = () => {
    const date = new Date(0);
    date.setSeconds(countdown);

    // 1970-01-01T00:00:01.000Z => 00:01
    return date.toISOString().split('.')[0].split('T00:')[1];
  };

  const [createBadesulPix, { loading: badesulCreatePixLoading }] = useMutation(CREATE_BADESUL_PIX, {
    onError: (error) => {
      formatAndNotificateError(error);
    },
    onCompleted: (d) => {
      setCurrentPix(d.createBadesulPix);
    },
  });

  const { loading: badesulPixLoading } = useQuery(GET_BADESUL_PIX, {
    variables: {
      loanId: id,
    },
    onCompleted: (d) => {
      if (!d.badesulPix) {
        createBadesulPix({
          variables: {
            loanId: id,
          },
        });
      }
      setCurrentPix(d.badesulPix);
    },
    onError: (error) => {
      formatAndNotificateError(error);
    },
  });

  const [confirmBadesulPixPayment, { loading: confirmingPixPaymentLoading }] = useMutation(
    CONFIRM_BADESUL_PIX_PAYMENT,
    {
      onError: (error) => {
        formatAndNotificateError(error);
      },
      onCompleted: (d) => {
        setCurrentPix(d.confirmBadesulPixPayment);
        contextRefetch();
      },
    },
  );

  const loading = badesulPixLoading || badesulCreatePixLoading || confirmingPixPaymentLoading;

  useEffect(() => {
    if (countdown !== 0) {
      setTimeout(() => setCountdown(countdown - 1), 1000);
    }
  }, [countdown]);

  useEffect(() => {
    if (currentPix?.expirationDate) {
      const expiration = new Date(currentPix.expirationDate);
      const now = new Date();
      const diffInSeconds = Math.floor((expiration.getTime() - now.getTime()) / 1000);
      setCountdown(diffInSeconds > 0 ? diffInSeconds : 0);
    }
  }, [currentPix]);

  const handleOkButton = () => {
    if (!currentPix) {
      return undefined;
    }

    if (Number(countdown) <= 0) {
      return {
        text: 'Gerar novo PIX',
        onClick: (e) => {
          e?.stopPropagation();
          createBadesulPix({
            variables: {
              loanId: id,
            },
          });
        },
      };
    }

    return {
      text: 'Já efetuei o pagamento',
      onClick: (e) => {
        e?.stopPropagation();
        confirmBadesulPixPayment({
          variables: {
            loanId: id,
          },
        });
      },
    };
  };

  const handleCancelButton = () => {
    if (!currentPix) {
      return undefined;
    }

    if (Number(countdown) <= 0) {
      return {
        text: 'Já efetuei o pagamento',
        onClick: (e) => {
          e?.stopPropagation();
          confirmBadesulPixPayment({
            variables: {
              loanId: id,
            },
          });
        },
      };
    }

    return undefined;
  };

  if (loading) {
    return (
      <Modal
        visible={visible}
        title="Pagamento via PIX"
        onClose={onClose}
        cancelButton={{
          text: 'Fechar',
          onClick: (e) => {
            e?.stopPropagation();
            onClose();
          },
        }}
        id="bank-slip-modal"
      >
        <Spin />
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      title="Pagamento via PIX"
      onClose={onClose}
      cancelButton={handleCancelButton()}
      okButton={handleOkButton()}
      className="badesul-pix-modal"
    >
      {!currentPix && (
        <div>
          Clique aqui para gerar o PIX
          <Button
            type="primary"
            onClick={() => {
              createBadesulPix({
                variables: {
                  loanId: id,
                },
              });
            }}
          >
            Gerar PIX
          </Button>
        </div>
      )}
      {currentPix && Number(countdown) > 0 && (
        <div className="content">
          <div>
            Vencimento:{' '}
            {new Date(currentPix.expirationDate).toLocaleString('pt-BR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </div>

          <div className="timer">
            Expira em: <span>{countdownAsDate()}</span>
          </div>

          <QRCodeCanvas value={currentPix.pixCode} size={1024} />

          <span>{currentPix.pixCode}</span>
          <Button
            color="link"
            icon={<CopyOutlined className="copy-button" />}
            className="copy-clipboard-button"
            onClick={() => {
              navigator.clipboard.writeText(currentPix.pixCode);
              message.info('Copiado!');
            }}
          >
            Copiar código PIX
          </Button>
        </div>
      )}
      {currentPix && countdown <= 0 && (
        <div className="content">
          <span>O PIX expirou.</span>
          <span>
            Caso o pagamento já tenha sido efetuado, clique em &quot;Já efetuei o pagamento&quot;
            para atualizar o status do pagamento.
          </span>
          <span>
            Caso contrário, clique em &quot;Gerar novo PIX&quot; para criar um novo código.
          </span>
        </div>
      )}
    </Modal>
  );
};

export default BadesulPixModal;
